<template>
  <div class="p-6">
    <!-- 页面标题和操作按钮 -->
    <div class="flex justify-between items-center mb-6">
      <div>
        <h1 class="text-2xl font-bold text-gray-900 dark:text-gray-100">套餐分类管理</h1>
        <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">管理套餐分类定义</p>
      </div>
      <button @click="handleCreateCategory"
        class="primary-btn gap-2">
        <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
        </svg>
        创建分类
      </button>
    </div>

    <!-- 分类列表 -->
    <div class="bg-white dark:bg-gray-800 rounded-lg shadow dark:shadow-gray-900">
      <CategoryList :categories="categories" @edit="handleEditCategory" @delete="handleDeleteCategory" />
    </div>

    <!-- 创建/编辑分类 Modal -->
    <Modal :show="showCategoryModal" @close="closeCategoryModal" title="套餐分类信息">
      <CategoryForm :category="currentCategory" :isEdit="isEditMode" @submit="handleCategorySubmit"
        @cancel="closeCategoryModal" />
    </Modal>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import CategoryList from '@/components/membership/CategoryList.vue'
import CategoryForm from '@/components/membership/CategoryForm.vue'
import Modal from '@/components/common/Modal.vue'
import { getAllCategoriesAPI, createCategoryAPI, updateCategoryAPI, deleteCategoryAPI } from '@/api/membership/category'
import type { PlanCategory, CreateCategoryRequest, UpdateCategoryRequest } from '@/types/membership/category'
import { displayLocalizedText } from '@/types/membership/localized'

/**
 * 分类列表
 */
const categories = ref<PlanCategory[]>([])

/**
 * 当前操作的分类
 */
const currentCategory = ref<PlanCategory | undefined>(undefined)

/**
 * 显示分类 Modal
 */
const showCategoryModal = ref(false)

/**
 * 是否为编辑模式
 */
const isEditMode = ref(false)

/**
 * 加载所有分类
 */
const loadCategories = async () => {
  try {
    const response = await getAllCategoriesAPI()
    categories.value = response.data.categories
  } catch (error) {
    console.error('加载分类失败:', error)
  }
}

/**
 * 创建分类
 */
const handleCreateCategory = () => {
  currentCategory.value = undefined
  isEditMode.value = false
  showCategoryModal.value = true
}

/**
 * 编辑分类
 */
const handleEditCategory = (category: PlanCategory) => {
  currentCategory.value = category
  isEditMode.value = true
  showCategoryModal.value = true
}

/**
 * 删除分类
 */
const handleDeleteCategory = async (category: PlanCategory) => {
  if (!confirm(`确定要删除分类"${displayLocalizedText(category.name)}"吗？\n\n注意：删除分类可能会影响已配置该分类的套餐。`)) {
    return
  }
  try {
    await deleteCategoryAPI(category.id)
    await loadCategories()
  } catch (error) {
    console.error('删除分类失败:', error)
  }
}

/**
 * 提交分类表单
 */
const handleCategorySubmit = async (data: CreateCategoryRequest | UpdateCategoryRequest) => {
  try {
    if (isEditMode.value && currentCategory.value) {
      await updateCategoryAPI(currentCategory.value.id, data as UpdateCategoryRequest)
    } else {
      await createCategoryAPI(data as CreateCategoryRequest)
    }
    await loadCategories()
    closeCategoryModal()
  } catch (error) {
    console.error('保存分类失败:', error)
  }
}

/**
 * 关闭分类 Modal
 */
const closeCategoryModal = () => {
  showCategoryModal.value = false
  currentCategory.value = undefined
}

/**
 * 组件挂载时加载数据
 */
onMounted(async () => {
  await loadCategories()
})
</script>
